/**
 * RBAC (Role-Based Access Control) configuration.
 * Defines roles and their permissions for route and sidebar access.
 * Add new roles here to extend access control in a scalable way.
 */

/** Supported roles */
export const ROLES = {
  FINANCE: 'finance',
  RMG: 'rmg',
  ADMIN: 'admin',
} as const;

export type RoleType = typeof ROLES[keyof typeof ROLES];

/** Default role when not specified (backward compatibility) */
export const DEFAULT_ROLE: RoleType = ROLES.FINANCE;

/** Valid role values */
export const VALID_ROLES = Object.values(ROLES) as RoleType[];

/** Display labels for roles (shown in UI) */
export const ROLE_DISPLAY_NAMES: Record<RoleType, string> = {
  [ROLES.ADMIN]: 'Admin',
  [ROLES.FINANCE]: 'Finance',
  [ROLES.RMG]: 'RMG',
};

/**
 * Get the display name for a role (Admin, Finance, RMG).
 * @param role - User role (finance, rmg, admin).
 */
export function formatRoleDisplay(role: RoleType | string | undefined): string {
  return ROLE_DISPLAY_NAMES[role as RoleType] || role || '';
}

/**
 * Route identifiers (paths after /v2 or legacy equivalents) for docs / sidebar.
 */
export const ROUTES = {
  CLIENT_ANALYTICS: '/',
  MY_DASHBOARD: '/my-dashboard',
  TIMESHEET_ANALYSIS: '/employee-analysis',
  PROJECT_ANALYSIS: '/project-analysis',
  CLIENT_ACQUISITION_RETENTION: '/client-acquisition-retention',
} as const;

/** v2 paths only admins may open (finance blocked). */
const V2_ADMIN_ONLY_PREFIXES = [
  '/admin-settings',
  '/rate-config',
  '/email-access',
];

/**
 * Map /v2/... to the same logical path used for RBAC (without /v2 prefix).
 */
export function normalizePathForRbac(pathname: string): string {
  if (pathname === '/v2' || pathname === '/v2/') {
    return '/';
  }
  if (pathname.startsWith('/v2/')) {
    return pathname.slice('/v2'.length);
  }
  return pathname;
}

/**
 * Sidebar section identifiers.
 */
export const SIDEBAR_ITEMS = {
  CLIENT_ANALYTICS: 'client-analytics',
  CLIENT_ACQUISITION_RETENTION: 'client-acquisition-retention',
  EMPLOYEE_ANALYSIS: 'employee-analysis',
  PROJECT_ANALYSIS: 'project-analysis',
  ACCOUNT_OWNER: 'account-owner',
  ORG_LEVEL: 'org-level',
  CLIENT_LEVEL: 'client-level',
  STRATEGIC_LEVEL: 'strategic-level',
  TIMESHEET: 'timesheet',
  AI_CHATBOT: 'ai-chatbot',
  RATE_CONFIG: 'rate-config',
  EMAIL_CONFIG: 'email-config',
} as const;

export type SidebarItemType = typeof SIDEBAR_ITEMS[keyof typeof SIDEBAR_ITEMS];

/**
 * Role-based permissions: which sidebar items each role can see.
 * finance: full analytics access (not Subscription management — admin only)
 * rmg: only Timesheet
 * admin: full access + Subscriptions & SaaS + Rate Config + Email Config
 */
const SIDEBAR_PERMISSIONS: Record<RoleType, SidebarItemType[]> = {
  [ROLES.FINANCE]: [
    SIDEBAR_ITEMS.CLIENT_ANALYTICS,
    SIDEBAR_ITEMS.CLIENT_ACQUISITION_RETENTION,
    SIDEBAR_ITEMS.EMPLOYEE_ANALYSIS,
    SIDEBAR_ITEMS.PROJECT_ANALYSIS,
    SIDEBAR_ITEMS.ACCOUNT_OWNER,
    SIDEBAR_ITEMS.ORG_LEVEL,
    SIDEBAR_ITEMS.CLIENT_LEVEL,
    SIDEBAR_ITEMS.STRATEGIC_LEVEL,
    SIDEBAR_ITEMS.TIMESHEET,
    SIDEBAR_ITEMS.AI_CHATBOT,
  ],
  [ROLES.RMG]: [
    SIDEBAR_ITEMS.TIMESHEET,
  ],
  [ROLES.ADMIN]: [
    SIDEBAR_ITEMS.CLIENT_ANALYTICS,
    SIDEBAR_ITEMS.CLIENT_ACQUISITION_RETENTION,
    SIDEBAR_ITEMS.EMPLOYEE_ANALYSIS,
    SIDEBAR_ITEMS.PROJECT_ANALYSIS,
    SIDEBAR_ITEMS.ACCOUNT_OWNER,
    SIDEBAR_ITEMS.ORG_LEVEL,
    SIDEBAR_ITEMS.CLIENT_LEVEL,
    SIDEBAR_ITEMS.STRATEGIC_LEVEL,
    SIDEBAR_ITEMS.TIMESHEET,
    SIDEBAR_ITEMS.AI_CHATBOT,
    SIDEBAR_ITEMS.RATE_CONFIG,
    SIDEBAR_ITEMS.EMAIL_CONFIG,
  ],
};

/**
 * Check if a role can see a sidebar item.
 * @param role - User role (finance, rmg, admin).
 * @param itemId - Sidebar item ID from SIDEBAR_ITEMS.
 */
export function canSeeSidebarItem(
  role: RoleType | string | undefined,
  itemId: string
): boolean {
  const r = (role || DEFAULT_ROLE) as RoleType;
  const allowed = SIDEBAR_PERMISSIONS[r] || SIDEBAR_PERMISSIONS[ROLES.FINANCE];
  return Array.isArray(allowed) && allowed.includes(itemId as SidebarItemType);
}

/**
 * Check if RMG role can access a normalized path.
 */
function canRmgAccessNormalizedPath(p: string, searchParams?: URLSearchParams): boolean {
  if (p === '/profile' || p.startsWith('/profile/')) {
    return true;
  }
  if (p === '/my-dashboard') {
    const category = searchParams?.get?.('category') || 'org-level';
    return category === 'timesheet';
  }
  if (
    p === '/employee-analysis'
    || p.startsWith('/employee-analysis/')
    || p === '/project-analysis'
    || p.startsWith('/project-analysis/')
    || p === '/timesheet-insights'
    || p === '/timesheet'
    || p === '/technology-billing-overview'
    || p === '/timesheet-analysis'
  ) {
    return true;
  }
  return false;
}

/**
 * Check if a role can access a route.
 * @param role - User role.
 * @param pathname - Full URL path (e.g. '/', '/v2/project-analysis').
 * @param searchParams - Optional; for /my-dashboard, category is checked.
 */
export function canAccessRoute(
  role: RoleType | string | undefined,
  pathname: string,
  searchParams?: URLSearchParams
): boolean {
  const r = (role || DEFAULT_ROLE) as RoleType;

  const p = normalizePathForRbac(pathname);

  if (pathname.startsWith('/v2') || pathname === '/v2') {
    if (r === ROLES.ADMIN) {
      return true;
    }
    if (r === ROLES.FINANCE) {
      for (let i = 0; i < V2_ADMIN_ONLY_PREFIXES.length; i++) {
        const pre = V2_ADMIN_ONLY_PREFIXES[i];
        if (p === pre || p.startsWith(`${pre}/`)) {
          return false;
        }
      }
      return true;
    }
    if (r === ROLES.RMG) {
      return canRmgAccessNormalizedPath(p, searchParams);
    }
    return false;
  }

  if (p === ROUTES.CLIENT_ANALYTICS) {
    return r === ROLES.FINANCE || r === ROLES.ADMIN;
  }
  if (p === ROUTES.CLIENT_ACQUISITION_RETENTION) {
    return r === ROLES.FINANCE || r === ROLES.ADMIN;
  }
  if (p === ROUTES.TIMESHEET_ANALYSIS || p.startsWith(`${ROUTES.TIMESHEET_ANALYSIS}/`)) {
    return r === ROLES.FINANCE || r === ROLES.ADMIN || r === ROLES.RMG;
  }
  if (p === ROUTES.PROJECT_ANALYSIS || p.startsWith(`${ROUTES.PROJECT_ANALYSIS}/`)) {
    return r === ROLES.FINANCE || r === ROLES.ADMIN || r === ROLES.RMG;
  }
  if (p === ROUTES.MY_DASHBOARD) {
    if (r === ROLES.RMG) {
      const category = searchParams?.get?.('category') || 'org-level';
      return category === 'timesheet';
    }
    return r === ROLES.FINANCE || r === ROLES.ADMIN;
  }

  /** Legacy bookmark before redirect to `/v2/employee-analysis`. */
  if (pathname === '/timesheet-analysis') {
    return r === ROLES.FINANCE || r === ROLES.ADMIN || r === ROLES.RMG;
  }

  return false;
}

/**
 * Get the default/first allowed route for a role (for redirect when unauthorized).
 */
export function getDefaultRouteForRole(role: RoleType | string | undefined): string {
  const r = (role || DEFAULT_ROLE) as RoleType;
  if (r === ROLES.RMG) {
    return '/v2/timesheet-insights';
  }
  return '/v2/client-management';
}

/**
 * Check if a role has admin config access (Rate Config, Email Config).
 */
export function hasAdminConfigAccess(role: RoleType | string | undefined): boolean {
  return (role as RoleType) === ROLES.ADMIN;
}
